'use client';

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Locale = 'fr' | 'en' | 'nl';

interface I18nState {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

export const useI18n = create<I18nState>()(
  persist(
    (set) => ({
      locale: 'fr',
      setLocale: (locale) => set({ locale }),
    }),
    { name: 'matar-locale' }
  )
);

// Translations dictionary
export const T: Record<Locale, Record<string, string>> = {
  fr: {
    // Nav
    'nav.atelier': "L'Atelier",
    'nav.about': 'À Propos',
    'nav.exception': 'Exception',
    'nav.boutique': 'Boutique',
    'nav.galerie': 'Galerie',
    'nav.tracabilite': 'Traçabilité',
    'nav.surmesure': 'Sur Mesure',
    'nav.chroniques': 'Chroniques',
    'nav.temoignages': 'Témoignages',
    'nav.contact': 'Contact',
    'nav.panier': 'Panier',
    'nav.favoris': 'Favoris',
    'nav.recherche': 'Recherche',
    'nav.compte': 'Compte',
    // Hero
    'hero.tagline': "L'Intelligence au service de l'Excellence.",
    'hero.cta.collections': 'Découvrir les Collections',
    'hero.cta.atelier': "Visiter l'Atelier",
    // Shop
    'shop.title.before': 'Composez votre',
    'shop.title.after': 'arithmétique du cuir.',
    'shop.eyebrow': 'La Boutique',
    'shop.sort': 'Trier',
    'shop.sort.featured': 'En vedette',
    'shop.sort.price-asc': 'Prix croissant',
    'shop.sort.price-desc': 'Prix décroissant',
    'shop.sort.rating': 'Meilleur avis',
    'shop.sort.name': 'Nom A → Z',
    'shop.add': 'Ajouter',
    'shop.added': 'Ajouté',
    'shop.categories': 'Catégories',
    'shop.all': 'Toutes',
    'shop.compare': 'Comparer',
    'shop.favorites': 'Cœur pour favoris',
    'shop.click_details': 'Cliquez pour détails',
    // Cart
    'cart.title': 'Votre Panier',
    'cart.empty': 'Votre panier est vide',
    'cart.empty.cta': 'Découvrir la boutique',
    'cart.empty.desc': 'Découvrez nos collections d\'exception et ajoutez vos pièces préférées pour commencer votre commande.',
    'cart.subtotal': 'Sous-total',
    'cart.total': 'Total',
    'cart.shipping': 'Livraison',
    'cart.shipping.free': 'Offerte dès 500 €',
    'cart.checkout': 'Passer la commande',
    'cart.articles': 'articles',
    'cart.article': 'article',
    // Product modal
    'product.details': 'Détails',
    'product.reviews': 'Avis',
    'product.shipping': 'Livraison',
    'product.add_to_cart': 'Ajouter',
    'product.guarantee': 'Garantie à vie',
    'product.numbered': 'Numérotée',
    'product.delivery': 'Livraison 24-48h',
    'product.verified_purchase': 'Achat vérifié',
    'product.no_reviews': 'Aucun avis pour l\'instant',
    // Checkout
    'checkout.title': 'Paiement sécurisé',
    'checkout.confirmed': 'Commande confirmée',
    'checkout.confirm': 'Confirmer',
    'checkout.delivery': 'Mode de livraison',
    'checkout.payment': 'Mode de paiement',
    'checkout.pickup': 'Retrait boutique',
    'checkout.belgium': 'Belgique',
    'checkout.europe': 'Europe',
    'checkout.world': 'Monde',
    'checkout.card': 'Carte bancaire',
    'checkout.transfer': 'Virement',
    'checkout.whatsapp': 'WhatsApp',
    // Wishlist
    'wishlist.title': 'Mes Favoris',
    'wishlist.empty': 'Vos favoris sont vides',
    'wishlist.empty.desc': 'Cliquez sur le cœur d\'un produit pour l\'ajouter ici. Votre sélection vous attend pour passer commande.',
    'wishlist.add_all': 'Tout ajouter au panier',
    'wishlist.clear': 'Vider mes favoris',
    // Auth
    'auth.login': 'Connexion',
    'auth.register': 'Créer un compte',
    'auth.profile': 'Mon Profil',
    'auth.email': 'Email',
    'auth.password': 'Mot de passe',
    'auth.firstname': 'Prénom',
    'auth.lastname': 'Nom',
    'auth.phone': 'Téléphone',
    'auth.login.cta': 'Se connecter',
    'auth.register.cta': 'Créer mon compte',
    'auth.logout': 'Se déconnecter',
    'auth.welcome': 'Bienvenue',
    'auth.account': 'Compte client',
    'auth.orders': 'Commandes',
    'auth.member_since': 'Membre depuis',
    'auth.no_account': 'Pas encore de compte ?',
    'auth.has_account': 'Vous avez déjà un compte ?',
    'auth.create_one': 'Créez-en un',
    'auth.login_instead': 'Connectez-vous',
    // Footer
    'footer.maison': 'La Maison',
    'footer.boutique': 'Boutique',
    'footer.legal': 'Mentions Légales',
    'footer.privacy': 'Confidentialité',
    'footer.rights': 'Tous droits réservés',
    // Common
    'common.close': 'Fermer',
    'common.search_placeholder': 'Rechercher un produit, une catégorie...',
    'common.no_results': 'Aucun résultat pour',
    'common.type_min': 'Tapez au moins 2 caractères',
  },
  en: {
    'nav.atelier': 'The Workshop',
    'nav.about': 'About',
    'nav.exception': 'Exception',
    'nav.boutique': 'Shop',
    'nav.galerie': 'Gallery',
    'nav.tracabilite': 'Traceability',
    'nav.surmesure': 'Bespoke',
    'nav.chroniques': 'Stories',
    'nav.temoignages': 'Reviews',
    'nav.contact': 'Contact',
    'nav.panier': 'Cart',
    'nav.favoris': 'Wishlist',
    'nav.recherche': 'Search',
    'nav.compte': 'Account',
    'hero.tagline': 'Intelligence in the service of Excellence.',
    'hero.cta.collections': 'Discover Collections',
    'hero.cta.atelier': 'Visit the Workshop',
    'shop.title.before': 'Compose your',
    'shop.title.after': 'leather arithmetic.',
    'shop.eyebrow': 'The Shop',
    'shop.sort': 'Sort',
    'shop.sort.featured': 'Featured',
    'shop.sort.price-asc': 'Price ascending',
    'shop.sort.price-desc': 'Price descending',
    'shop.sort.rating': 'Top rated',
    'shop.sort.name': 'Name A → Z',
    'shop.add': 'Add',
    'shop.added': 'Added',
    'shop.categories': 'Categories',
    'shop.all': 'All',
    'shop.compare': 'Compare',
    'shop.favorites': 'Heart for favorites',
    'shop.click_details': 'Click for details',
    'cart.title': 'Your Cart',
    'cart.empty': 'Your cart is empty',
    'cart.empty.cta': 'Discover the shop',
    'cart.empty.desc': 'Explore our exceptional collections and add your favorite pieces to start your order.',
    'cart.subtotal': 'Subtotal',
    'cart.total': 'Total',
    'cart.shipping': 'Shipping',
    'cart.shipping.free': 'Free over €500',
    'cart.checkout': 'Checkout',
    'cart.articles': 'items',
    'cart.article': 'item',
    'product.details': 'Details',
    'product.reviews': 'Reviews',
    'product.shipping': 'Shipping',
    'product.add_to_cart': 'Add to cart',
    'product.guarantee': 'Lifetime warranty',
    'product.numbered': 'Numbered',
    'product.delivery': '24-48h delivery',
    'product.verified_purchase': 'Verified purchase',
    'product.no_reviews': 'No reviews yet',
    'checkout.title': 'Secure payment',
    'checkout.confirmed': 'Order confirmed',
    'checkout.confirm': 'Confirm',
    'checkout.delivery': 'Delivery method',
    'checkout.payment': 'Payment method',
    'checkout.pickup': 'Store pickup',
    'checkout.belgium': 'Belgium',
    'checkout.europe': 'Europe',
    'checkout.world': 'Worldwide',
    'checkout.card': 'Credit card',
    'checkout.transfer': 'Bank transfer',
    'checkout.whatsapp': 'WhatsApp',
    'wishlist.title': 'My Favorites',
    'wishlist.empty': 'Your favorites are empty',
    'wishlist.empty.desc': 'Click the heart on a product to add it here. Your selection awaits your order.',
    'wishlist.add_all': 'Add all to cart',
    'wishlist.clear': 'Clear favorites',
    'auth.login': 'Sign in',
    'auth.register': 'Create account',
    'auth.profile': 'My Profile',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.firstname': 'First name',
    'auth.lastname': 'Last name',
    'auth.phone': 'Phone',
    'auth.login.cta': 'Sign in',
    'auth.register.cta': 'Create my account',
    'auth.logout': 'Sign out',
    'auth.welcome': 'Welcome',
    'auth.account': 'Customer account',
    'auth.orders': 'Orders',
    'auth.member_since': 'Member since',
    'auth.no_account': 'No account yet?',
    'auth.has_account': 'Already have an account?',
    'auth.create_one': 'Create one',
    'auth.login_instead': 'Sign in',
    'footer.maison': 'The House',
    'footer.boutique': 'Shop',
    'footer.legal': 'Legal Notice',
    'footer.privacy': 'Privacy',
    'footer.rights': 'All rights reserved',
    'common.close': 'Close',
    'common.search_placeholder': 'Search a product, a category...',
    'common.no_results': 'No results for',
    'common.type_min': 'Type at least 2 characters',
  },
  nl: {
    'nav.atelier': 'Atelier',
    'nav.about': 'Over Ons',
    'nav.exception': 'Uitzondering',
    'nav.boutique': 'Winkel',
    'nav.galerie': 'Galerij',
    'nav.tracabilite': 'Traceerbaarheid',
    'nav.surmesure': 'Op Maat',
    'nav.chroniques': 'Verhalen',
    'nav.temoignages': 'Getuigenissen',
    'nav.contact': 'Contact',
    'nav.panier': 'Mandje',
    'nav.favoris': 'Favorieten',
    'nav.recherche': 'Zoeken',
    'nav.compte': 'Account',
    'hero.tagline': 'Intelligentie in dienst van Uitmuntendheid.',
    'hero.cta.collections': 'Ontdek Collecties',
    'hero.cta.atelier': 'Bezoek Atelier',
    'shop.title.before': 'Stel uw',
    'shop.title.after': 'leder rekenkunde samen.',
    'shop.eyebrow': 'De Winkel',
    'shop.sort': 'Sorteer',
    'shop.sort.featured': 'Uitgelicht',
    'shop.sort.price-asc': 'Prijs oplopend',
    'shop.sort.price-desc': 'Prijs aflopend',
    'shop.sort.rating': 'Best beoordeeld',
    'shop.sort.name': 'Naam A → Z',
    'shop.add': 'Toevoegen',
    'shop.added': 'Toegevoegd',
    'shop.categories': 'Categorieën',
    'shop.all': 'Alle',
    'shop.compare': 'Vergelijken',
    'shop.favorites': 'Hart voor favorieten',
    'shop.click_details': 'Klik voor details',
    'cart.title': 'Uw Mandje',
    'cart.empty': 'Uw mandje is leeg',
    'cart.empty.cta': 'Ontdek de winkel',
    'cart.empty.desc': 'Ontdek onze uitzonderlijke collecties en voeg uw favoriete stukken toe om uw bestelling te starten.',
    'cart.subtotal': 'Subtotaal',
    'cart.total': 'Totaal',
    'cart.shipping': 'Verzending',
    'cart.shipping.free': 'Gratis vanaf €500',
    'cart.checkout': 'Afrekenen',
    'cart.articles': 'artikelen',
    'cart.article': 'artikel',
    'product.details': 'Details',
    'product.reviews': 'Recensies',
    'product.shipping': 'Verzending',
    'product.add_to_cart': 'In mandje',
    'product.guarantee': 'Levenslange garantie',
    'product.numbered': 'Genummerd',
    'product.delivery': '24-48u levering',
    'product.verified_purchase': 'Geverifieerde aankoop',
    'product.no_reviews': 'Nog geen recensies',
    'checkout.title': 'Veilige betaling',
    'checkout.confirmed': 'Bestelling bevestigd',
    'checkout.confirm': 'Bevestig',
    'checkout.delivery': 'Leveringsmethode',
    'checkout.payment': 'Betalingsmethode',
    'checkout.pickup': 'Winkel afhaling',
    'checkout.belgium': 'België',
    'checkout.europe': 'Europa',
    'checkout.world': 'Wereldwijd',
    'checkout.card': 'Kredietkaart',
    'checkout.transfer': 'Overschrijving',
    'checkout.whatsapp': 'WhatsApp',
    'wishlist.title': 'Mijn Favorieten',
    'wishlist.empty': 'Uw favorieten zijn leeg',
    'wishlist.empty.desc': 'Klik op het hart van een product om het hier toe te voegen. Uw selectie wacht op uw bestelling.',
    'wishlist.add_all': 'Alles toevoegen aan mandje',
    'wishlist.clear': 'Favorieten wissen',
    'auth.login': 'Aanmelden',
    'auth.register': 'Account maken',
    'auth.profile': 'Mijn Profiel',
    'auth.email': 'Email',
    'auth.password': 'Wachtwoord',
    'auth.firstname': 'Voornaam',
    'auth.lastname': 'Achternaam',
    'auth.phone': 'Telefoon',
    'auth.login.cta': 'Aanmelden',
    'auth.register.cta': 'Mijn account maken',
    'auth.logout': 'Afmelden',
    'auth.welcome': 'Welkom',
    'auth.account': 'Klantenaccount',
    'auth.orders': 'Bestellingen',
    'auth.member_since': 'Lid sinds',
    'auth.no_account': 'Nog geen account?',
    'auth.has_account': 'Al een account?',
    'auth.create_one': 'Maak er een',
    'auth.login_instead': 'Aanmelden',
    'footer.maison': 'Het Huis',
    'footer.boutique': 'Winkel',
    'footer.legal': 'Juridische Kennisgeving',
    'footer.privacy': 'Privacy',
    'footer.rights': 'Alle rechten voorbehouden',
    'common.close': 'Sluiten',
    'common.search_placeholder': 'Zoek een product, een categorie...',
    'common.no_results': 'Geen resultaten voor',
    'common.type_min': 'Typ minstens 2 tekens',
  },
};

export function useTranslation() {
  const { locale } = useI18n();
  return {
    t: (key: string) => T[locale]?.[key] ?? T.fr[key] ?? key,
    locale,
  };
}

export const LOCALES: { code: Locale; label: string; flag: string }[] = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'nl', label: 'Nederlands', flag: '🇧🇪' },
];
